package dev.krno.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import dev.krno.data.EventData
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Canvas
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Persona-5-flavored hero, coloured in the Kotlin/JetBrains palette (purple + pink + orange
 * on near-black). Structurally different from the base Hero — this is a full-viewport
 * theatrical slab rather than the compact D layout.
 *
 * The Rive stage / canvas element IDs are the same as base Hero (#stage, #kodee) so the
 * existing bootRive() effect in Hero.kt continues to work when we mount both. In this
 * prototype we call the boot inline so the persona hero is self-contained.
 */
@Composable
fun HeroPersona() {
    Section(attrs = { classes("hero-p") }) {
        // Diagonal slab that reads as a P5 "info card" pinned behind the copy.
        Div(attrs = { classes("hp-slab") })
        // Grain layer is applied at body ::before in CSS.

        // Kotlin 15 ribbons across the top of the hero. Each track holds an even number
        // of identical runs so the -50% marquee loops seamlessly; half a track must
        // stay wider than a 2560px screen or a gap opens at the right edge.
        Div(attrs = {
            classes("hp-ribbons")
            attr("aria-hidden", "true")
        }) {
            Div(attrs = { classes("hp-ribbon", "hp-ribbon-back") }) {
                Div(attrs = { classes("hp-ribbon-track") }) {
                    repeat(16) {
                        Span { Text("Happy birthday, Kotlin") }
                        Span { Text("2011 → 2026") }
                    }
                }
            }
            Div(attrs = { classes("hp-ribbon", "hp-ribbon-front") }) {
                Div(attrs = { classes("hp-ribbon-track") }) {
                    repeat(14) {
                        Span { Text("Kotlin turns 15") }
                        Span { Text("·") }
                        Span { Text("Birthday party") }
                        Span { Text("·") }
                    }
                }
            }
        }

        Div(attrs = { classes("hp-grid") }) {
            Div(attrs = { classes("hp-copy") }) {
                Div(attrs = { classes("hp-eyebrow") }) {
                    Span(attrs = { classes("hp-chip-num") }) { Text("#1") }
                    Span(attrs = { classes("hp-eyebrow-label") }) {
                        Text("Brno Kotlin")
                        Span(attrs = { classes("reg") }) { Text("®") }
                        Text(" Meetup")
                    }
                }
                H1(attrs = { classes("hp-title") }) {
                    Span(attrs = { classes("hp-title-brn") }) {
                        "Brn".forEachIndexed { i, ch ->
                            Span(attrs = {
                                classes("hp-letter")
                                style { property("--i", i.toString()) }
                            }) { Text(ch.toString()) }
                        }
                    }
                    Em(attrs = { classes("hp-title-ko") }) {
                        "Ko".forEachIndexed { i, ch ->
                            Span(attrs = {
                                classes("hp-letter")
                                style { property("--i", (i + 3).toString()) }
                            }) { Text(ch.toString()) }
                        }
                    }
                }
                Div(attrs = { classes("hp-sub") }) {
                    Text("Talks, networking and Kotlin's 15th Birthday Party!")
                }
                Div(attrs = { classes("hp-chips") }) {
                    Span(attrs = { classes("hp-chip", "hp-chip-key") }) {
                        Text(EventData.EVENT_DATE_TIME)
                    }
                    Span(attrs = { classes("hp-chip") }) { Text(EventData.VENUE) }
                    Span(attrs = { classes("hp-chip") }) { Text("Free entry") }
                }
                Div(attrs = { classes("hp-cta") }) {
                    A(href = "#register", attrs = { classes("hp-btn", "hp-btn-fill") }) {
                        Text("Save your seat")
                        Span(attrs = { classes("hp-arrow") }) { Text("→") }
                    }
                    A(href = "#program", attrs = { classes("hp-btn", "hp-btn-out") }) {
                        Text("See the program")
                    }
                }
                P(attrs = { classes("hp-note") }) {
                    Text("doors 17:30, first talk 18:10")
                }
            }

            Div(attrs = { classes("hp-art") }) {
                // Stage + KODEE tag sit side-by-side in a flex row so the label
                // reads as a name plate pinned to the container. Compact snippet
                // stays as a block below the row.
                Div(attrs = { classes("hp-stage-row") }) {
                    Div(attrs = { classes("hp-stage-frame") }) {
                        Div(attrs = {
                            id("stage")
                            classes("stage", "hp-stage")
                        }) {
                            Canvas(attrs = {
                                id("kodee")
                                attr("width", "1120")
                                attr("height", "1120")
                            })
                            // Poster of Idle frame 0 — not the editor rest pose, which
                            // differs (flame_tongue is 100 at rest, 55 in Idle). Matching
                            // frame 0 means the handover needs no cross-fade. The
                            // Rive runtime is ~813 KB and lands ~8s after this on a
                            // throttled phone; without a poster the stage sits empty
                            // that whole time. Hidden by .stage.loaded.
                            Div(attrs = { classes("stage-fallback") }) {
                                Img(src = "assets/kodee-poster.webp?v=2", attrs = {
                                    attr("alt", "Kodee riding a rocket")
                                    attr("width", "768")
                                    attr("height", "768")
                                    attr("decoding", "async")
                                })
                            }
                        }
                    }
                    Div(attrs = { classes("hp-tag-corner") }) { Text("KODEE") }
                }
                CompactCodeSnippet()
            }
        }
    }

    // Reuse the base bootRive() — the canvas / stage IDs are identical.
    DisposableEffect(Unit) {
        val cleanup = dev.krno.rive.bootKodeeRive()
        onDispose { cleanup() }
    }
}

// CompactCodeSnippet is now shared with Speakers.kt — see components/CodeSnippet.kt.
